/**
 * Main-thread client that wraps a dedicated EasyParser web worker.
 *
 * For molecules above `WORKER_ATOM_THRESHOLD` atoms the client sends
 * batched commands to the worker; below the threshold the original
 * synchronous EasyParser classes are used directly (no overhead).
 *
 * Usage:
 *   const client = EasyParserWorkerClient.getInstance();
 *   const handle = await client.createParser(fileInfo);
 *   const atoms  = await client.getAtoms(handle);
 *   await client.destroyParser(handle);
 *
 * Handles are caller-chosen strings.  For convenience, `createParser`
 * auto-generates one and returns it.
 */
import { randomID } from "@/Core/Utils/MiscUtils";
import { createEasyParserWorker } from "./EasyParserWorkerFactory";
/**
 * Atom-count threshold above which parsing is offloaded to the worker.
 * Below this the synchronous main-thread parser is used for zero overhead.
 */
export const WORKER_ATOM_THRESHOLD = 1_000;
/**
 * Client for communicating with the EasyParser web worker.
 */
export class EasyParserWorkerClient {
    static _instance = null;
    _worker;
    _nextRequestId = 0;
    _pending = new Map();
    /**
     * Private constructor.
     */
    constructor() {
        // Worker instantiation is delegated to an isolated factory module so
        // that the `import.meta.url` reference (invalid under ts-jest's
        // CommonJS resolution) lives in a single file that Jest can mock.
        this._worker = createEasyParserWorker();
        this._worker.onmessage = this._onMessage.bind(this);
    }
    /**
     * Return the singleton client, lazily creating the worker on first call.
     *
     * @returns {EasyParserWorkerClient}  The shared client instance.
     */
    static getInstance() {
        if (!EasyParserWorkerClient._instance) {
            EasyParserWorkerClient._instance =
                new EasyParserWorkerClient();
        }
        return EasyParserWorkerClient._instance;
    }
    /**
     * Terminate the background worker and clear the singleton.
     * Useful for tests or application shutdown.
     */
    dispose() {
        this._worker.terminate();
        this._pending.clear();
        EasyParserWorkerClient._instance = null;
    }
    // ── High-level convenience methods ──────────────────────────────
    /**
     * Create an EasyParser in the worker from file contents.
     *
     * @param {IFileInfo} src     The file info (name + contents) to parse.
     * @param {string}    [handle]  Optional caller-chosen handle.
     * @returns {Promise<string>}  The handle for subsequent commands.
     */
    async createParser(src, handle) {
        const h = handle ?? `ep_${randomID()}`;
        await this._send([{ op: "createParser", handle: h, src }]);
        return h;
    }
    /**
     * Create an EasyParser in the worker from an already-parsed atom array.
     *
     * @param {IAtom[]}  atoms    The atoms to register.
     * @param {string}   [handle] Optional caller-chosen handle.
     * @returns {Promise<string>}  The handle for subsequent commands.
     */
    async createParserFromAtoms(atoms, handle) {
        const h = handle ?? `ep_${randomID()}`;
        await this._send([
            { op: "createParserFromAtoms", handle: h, atoms },
        ]);
        return h;
    }
    /**
     * Release a parser instance inside the worker.
     *
     * @param {string} handle  The parser handle to destroy.
     */
    async destroyParser(handle) {
        await this._send([{ op: "destroyParser", handle }]);
    }
    /**
     * Retrieve all atoms from the parser.
     *
     * @param {string} handle  The parser handle.
     * @returns {Promise<IAtom[]>}  The full atom array.
     */
    async getAtoms(handle) {
        const resp = await this._send([{ op: "getAtoms", handle }]);
        return resp.results[0];
    }
    /**
     * Retrieve a single atom by index.
     *
     * @param {string} handle  The parser handle.
     * @param {number} index   Zero-based atom index.
     * @returns {Promise<IAtom>}  The atom at the given index.
     */
    async getAtom(handle, index) {
        const resp = await this._send([
            { op: "getAtom", handle, index },
        ]);
        return resp.results[0];
    }
    /**
     * Get the number of atoms.
     *
     * @param {string} handle  The parser handle.
     * @returns {Promise<number>}  Atom count.
     */
    async getLength(handle) {
        const resp = await this._send([{ op: "getLength", handle }]);
        return resp.results[0];
    }
    /**
     * Get the axis-aligned bounding box of the parser's atoms.
     *
     * @param {string} handle  The parser handle.
     * @param {number} [stride]  Sampling stride.
     * @returns {Promise<BoundsResult | null>}  Bounds or null if no coords.
     */
    async getBounds(handle, stride) {
        const resp = await this._send([
            { op: "getBounds", handle, stride },
        ]);
        return resp.results[0];
    }
    /**
     * Select atoms matching a selection dictionary.
     *
     * @param {string}                       handle   The parser handle.
     * @param {{ [key: string]: string[] }}  sel      Selection criteria.
     * @param {boolean}                      [extract]  If true, matched atoms
     *     are removed from the parser's internal list (mutating operation).
     * @returns {Promise<IAtom[]>}  The selected atoms.
     */
    async selectedAtoms(handle, sel, extract = false) {
        const resp = await this._send([
            { op: "selectedAtoms", handle, sel, extract },
        ]);
        return resp.results[0];
    }
    /**
     * Check whether any atom in one parser is within a distance of any atom
     * in another parser.
     *
     * @param {string} handle       First parser handle.
     * @param {string} otherHandle  Second parser handle (must already exist).
     * @param {number} distance     Distance threshold in angstroms.
     * @param {number} [selfStride]  Stride for self atoms.
     * @param {number} [otherStride] Stride for other atoms.
     * @returns {Promise<boolean>}  True if any pair is within distance.
     */
    async isWithinDistance(handle, otherHandle, distance, selfStride, otherStride) {
        const resp = await this._send([
            {
                op: "isWithinDistance",
                handle,
                otherHandle,
                distance,
                selfStride,
                otherStride,
            },
        ]);
        return resp.results[0];
    }
    /**
     * Check if the molecule is flat (all coords zero in one dimension).
     *
     * @param {string} handle  The parser handle.
     * @returns {Promise<boolean>} True if all atoms have zero X or all have
     *     zero Y or all have zero Z.
     */
    async isFlat(handle) {
        const resp = await this._send([{ op: "isFlat", handle }]);
        return resp.results[0];
    }
    /**
     * Check if any atom is hydrogen.
     *
     * @param {string} handle  The parser handle.
     * @returns {Promise<boolean>}  True if at least one hydrogen atom exists.
     */
    async hasHydrogens(handle) {
        const resp = await this._send([
            { op: "hasHydrogens", handle },
        ]);
        return resp.results[0];
    }
    /**
     * Get unique residue names and IDs.
     *
     * @param {string} handle  The parser handle.
     * @returns {Promise<UniqueResiduesResult>} Unique residue names and IDs.
     */
    async getUniqueResidues(handle) {
        const resp = await this._send([
            { op: "getUniqueResidues", handle },
        ]);
        return resp.results[0];
    }
    /**
     * Append atoms to an existing parser.
     *
     * @param {string}         handle  The parser handle.
     * @param {IAtom | IAtom[]} atoms   Atoms to append.
     */
    async appendAtoms(handle, atoms) {
        await this._send([{ op: "appendAtoms", handle, atoms }]);
    }
    // ── Bulk convenience methods ────────────────────────────────────
    // Not called from anywhere.
    // /**
    //  * Creates multiple parsers from IFileInfo sources in parallel, returning
    //  * all handles once every parser is ready. Each parser is created via a
    //  * separate worker command, but all commands are dispatched concurrently
    //  * so the worker processes them back-to-back.
    //  *
    //  * @param {IFileInfo[]} sources  The file info objects to parse.
    //  * @returns {Promise<string[]>}  The handles, one per source, in order.
    //  */
    // public async createParsersFromFiles(
    //     sources: IFileInfo[]
    // ): Promise<string[]> {
    //     const promises = sources.map((src) => this.createParser(src));
    //     return Promise.all(promises);
    // }
    // Not called from anywhere.
    // /**
    //  * Creates multiple parsers from IAtom arrays in parallel.
    //  *
    //  * @param {IAtom[][]} atomArrays  The atom arrays to register.
    //  * @returns {Promise<string[]>}  The handles, one per array, in order.
    //  */
    // public async createParsersFromAtomArrays(
    //     atomArrays: IAtom[][]
    // ): Promise<string[]> {
    //     const promises = atomArrays.map((atoms) =>
    //         this.createParserFromAtoms(atoms)
    //     );
    //     return Promise.all(promises);
    // }
    // Not called from anywhere
    // /**
    //  * Retrieves bounds for multiple parser handles in parallel.
    //  *
    //  * @param {string[]} handles  The parser handles.
    //  * @param {number}   [stride]  Optional sampling stride.
    //  * @returns {Promise<(BoundsResult | null)[]>}  Bounds per handle, in order.
    //  */
    // public async getBoundsMultiple(
    //     handles: string[],
    //     stride?: number
    // ): Promise<(BoundsResult | null)[]> {
    //     const promises = handles.map((h) => this.getBounds(h, stride));
    //     return Promise.all(promises);
    // }
    // Not called from anywhere.
    // /**
    //  * Retrieves unique residues for multiple parser handles in parallel.
    //  *
    //  * @param {string[]} handles  The parser handles.
    //  * @returns {Promise<UniqueResiduesResult[]>}  Results per handle, in order.
    //  */
    // public async getUniqueResiduesMultiple(
    //     handles: string[]
    // ): Promise<UniqueResiduesResult[]> {
    //     const promises = handles.map((h) => this.getUniqueResidues(h));
    //     return Promise.all(promises);
    // }
    // Not called from anywhere
    // /**
    //  * Destroys multiple parser handles in parallel. Errors are suppressed
    //  * to ensure cleanup completes for all handles even if one fails.
    //  *
    //  * @param {string[]} handles  The parser handles to destroy.
    //  */
    // public async destroyParsers(handles: string[]): Promise<void> {
    //     await Promise.allSettled(
    //         handles.map((h) => this.destroyParser(h))
    //     );
    // }
    // ── Batch API (advanced) ────────────────────────────────────────
    /**
     * Send an arbitrary batch of commands in a single postMessage round-trip.
     * Callers that need to chain multiple operations efficiently should use
     * this instead of calling convenience methods one at a time.
     *
     * @param {EasyParserWorkerCmd[]} commands  The commands to execute.
     * @returns {Promise<EasyParserWorkerResponse>}  Response with one result
     *     per command.
     */
    sendBatch(commands) {
        return this._send(commands);
    }
    // ── Internals ───────────────────────────────────────────────────
    /**
     * Post a batch of commands to the worker and return the response promise.
     *
     * @param {EasyParserWorkerCmd[]} commands  The commands to send.
     * @returns {Promise<EasyParserWorkerResponse>}  The worker's response.
     */
    _send(commands) {
        const requestId = this._nextRequestId++;
        const request = { requestId, commands };
        return new Promise((resolve) => {
            this._pending.set(requestId, resolve);
            this._worker.postMessage(request);
        });
    }
    /**
     * Handle incoming messages from the worker thread.
     *
     * @param {MessageEvent<EasyParserWorkerResponse>} event  The worker
     *     response.
     */
    _onMessage(event) {
        const resp = event.data;
        const resolve = this._pending.get(resp.requestId);
        if (resolve) {
            this._pending.delete(resp.requestId);
            resolve(resp);
        }
    }
}
